import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';

export type SmartReminderType = 'hotel_address' | 'meeting_point' | 'screenshot_plan' | 'backup_indoor';

export type SmartReminder = {
  id: string;
  type: SmartReminderType;
  title: string;
  description: string;
  actionLabel?: string;
  actionType?: 'copy' | 'screenshot' | 'navigate';
};

const DISMISSED_KEY_PREFIX = '@tripflow_smart_reminders_dismissed:';

const dayKey = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString().slice(0, 10);
};

const todayKey = () => dayKey(new Date());

export async function getDismissedReminderIds(tripId: string): Promise<Set<string>> {
  try {
    const raw = await AsyncStorage.getItem(`${DISMISSED_KEY_PREFIX}${tripId}`);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    const ids = Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    return new Set(ids);
  } catch {
    return new Set();
  }
}

export async function dismissReminder(tripId: string, reminderId: string): Promise<void> {
  const key = `${DISMISSED_KEY_PREFIX}${tripId}`;
  const current = await getDismissedReminderIds(tripId);
  current.add(reminderId);
  await AsyncStorage.setItem(key, JSON.stringify(Array.from(current)));
}

export async function clearDismissedReminders(tripId: string): Promise<void> {
  await AsyncStorage.removeItem(`${DISMISSED_KEY_PREFIX}${tripId}`);
}

function getHotelAddress(itineraryItems: ItineraryItem[]): string | null {
  const hotels = itineraryItems.filter((i) => i.category === 'hotel');
  if (hotels.length === 0) return null;
  const best = hotels[0];
  const addr = (best.address || best.location || '').trim();
  return addr || null;
}

export async function getActiveReminders(
  trip: Trip,
  itineraryItems: ItineraryItem[],
  options?: { collaboratorCount?: number },
): Promise<SmartReminder[]> {
  const dismissed = await getDismissedReminderIds(trip.id);
  const reminders: SmartReminder[] = [];

  const start = new Date(trip.startDate);
  const end = new Date(trip.endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isDuringTrip = today >= start && today <= end;
  const isWithin48h = Math.abs(start.getTime() - today.getTime()) <= 48 * 60 * 60 * 1000;

  // 1) Save hotel address offline (high-signal, low-noise)
  const hotel = getHotelAddress(itineraryItems);
  if (hotel && !dismissed.has('hotel_address')) {
    reminders.push({
      id: 'hotel_address',
      type: 'hotel_address',
      title: 'Save hotel address offline',
      description: 'Copy your hotel address so you can find it without internet.',
      actionLabel: 'Copy address',
      actionType: 'copy',
    });
  }

  // 2) Meeting point (only relevant when >1 traveler/collaborator)
  const collabCount = options?.collaboratorCount ?? 0;
  if (collabCount > 0 && !dismissed.has('meeting_point')) {
    reminders.push({
      id: 'meeting_point',
      type: 'meeting_point',
      title: 'Set a family meetup point',
      description: 'Pick an easy landmark everyone can find if you get separated.',
    });
  }

  // 3) Screenshot today's plan (only during trip)
  if (isDuringTrip && !dismissed.has(`screenshot_plan:${todayKey()}`)) {
    reminders.push({
      id: `screenshot_plan:${todayKey()}`,
      type: 'screenshot_plan',
      title: 'Screenshot today’s plan',
      description: 'A quick screenshot helps when your signal drops.',
      actionLabel: 'Got it',
      actionType: 'screenshot',
    });
  }

  // 4) Backup indoor option (lightweight prompt close to trip start)
  if ((isWithin48h || isDuringTrip) && !dismissed.has('backup_indoor')) {
    reminders.push({
      id: 'backup_indoor',
      type: 'backup_indoor',
      title: 'Add a backup indoor option',
      description: 'Great for rainy days or when plans change.',
    });
  }

  return reminders;
}

